import { computed, inject } from "@angular/core";
import { patchState, signalStore, withComputed, withHooks, withMethods, withState } from "@ngrx/signals";
import { tapResponse } from "@ngrx/operators";
import { rxMethod } from "@ngrx/signals/rxjs-interop";
import { pipe, switchMap, tap } from "rxjs";
import {
	hasOptions,
	SimpleStaticVariable,
	TemplateVariable,
	UserDefinedTemplateVariable,
	VariableWithOptions,
} from "../models/dynamic-variable.model";
import { MessageTemplateVariableService } from "../services/message-template-variable.lib.service";
import { NotificationService } from "libs/tm-common/src/lib/services";
import { BUILT_IN_VARIABLES } from "../constants/template-variables";

type MessageTemplateVariableState = {
	variables: TemplateVariable[];
	isLoading: boolean;
	isSaving: boolean;
	saveError: string | null;
	error: string | null;
};

const initialState: MessageTemplateVariableState = {
	variables: [],
	isLoading: false,
	isSaving: false,
	saveError: null,
	error: null,
};

export const MessageTemplateVariableStore = signalStore(
	{ providedIn: "root" },
	withState(initialState),
	withComputed((store) => ({
		allowedVariableNames: computed(() => store.variables().map((v) => v.name)),

		variablesWithOptions: computed(() => store.variables().filter(hasOptions) as VariableWithOptions[]),

		simpleStaticVariables: computed(
			() => store.variables().filter((v) => v.kind === "simple-static") as SimpleStaticVariable[]
		),

		userDefinedVariables: computed(
			() => store.variables().filter((v) => !v.builtIn) as UserDefinedTemplateVariable[]
		),

		variablesByName: computed(() => {
			const map = new Map<string, TemplateVariable>();
			for (const v of store.variables()) {
				map.set(v.name, v);
			}
			return map;
		}),
	})),
	withMethods(
		(
			store,
			service = inject(MessageTemplateVariableService),
			notifications = inject(NotificationService)
		) => ({
			fetchVariables: rxMethod<void>(
				pipe(
					tap(() => patchState(store, { isLoading: true })),
					switchMap(() =>
						service.getUserDefinedVariables().pipe(
							tapResponse({
								next: (userDefinedVariables: UserDefinedTemplateVariable[]) =>
									patchState(store, {
										variables: [...BUILT_IN_VARIABLES, ...userDefinedVariables],
										isLoading: false,
										error: null,
									}),
								error: (error: Error) =>
									patchState(store, {
										variables: [...BUILT_IN_VARIABLES],
										error: error.message,
										isLoading: false,
									}),
							})
						)
					)
				)
			),

			saveUserDefinedVariables: rxMethod<UserDefinedTemplateVariable[]>(
				pipe(
					tap(() => patchState(store, { isSaving: true, saveError: null })),
					switchMap((variables) =>
						service.updateVariables(variables).pipe(
							tapResponse({
								next: (userDefinedVariables: UserDefinedTemplateVariable[]) => {
									patchState(store, {
										variables: [...BUILT_IN_VARIABLES, ...userDefinedVariables],
										isSaving: false,
										saveError: null,
									});
									notifications.showSuccessMessage("MessageTemplates.VariablesSaved", 3000);
								},
								error: (error: unknown) => {
									const message = (error as any)?.message ?? "Unknown error";
									patchState(store, { isSaving: false, saveError: message });
								},
							})
						)
					)
				)
			),

			isDialogVariable(name: string): boolean {
				const v = store.variablesByName().get(name);
				return !!v && v.kind !== "bound-static";
			},
		})
	),
	withHooks({
		onInit(store) {
			store.fetchVariables();
		},
	})
);
